import React, { useEffect, useState } from "react";
import useWindowSize from "../../../hooks/use-window-size";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../providers/theme-provider";
import spacings from "../../../theme/spacings";
import NavbarSmall from "../navbar/navbar-small";
import NavbarLarge from "../navbar/navbar-large";
import { motion } from "framer-motion";
import { fontSizes } from "../../../theme/font-sizes";
import CtaButton from "../../buttons/cta-button/cta-button";
import useSmoothScroll from "../../../hooks/use-smooth-scroll";
import { assetIcons, assetImages } from "../../../constants/assets";
import FloatingNavbar from "../navbar/floating-navbar";

const Hero = () => {
  const { width } = useWindowSize();
  const isMobile = width < 1024;
  const isSmall = width < 768;

  const { colors } = useTheme();

  const { t } = useTranslation();
  const handleScroll = useSmoothScroll();

  const paddingLeftRight = isMobile
    ? spacings.leftRightSpacingMobile
    : spacings.leftRightSpacing;

  // const staggerContainer = {
  //   visible: { transition: { staggerChildren: 0.15 } },
  // };

  // Fade-in animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 2.8 } },
  };

  return (
    <motion.div
      className="xl:min-h-screen relative"
      style={{
        backgroundColor: colors.heroBg,
        paddingLeft: paddingLeftRight,
        paddingRight: paddingLeftRight,
      }}
      initial="hidden"
      animate="visible"
      // variants={staggerContainer}
      variants={fadeIn}
      id="home"
    >
      <FloatingNavbar />
      {isSmall ? <NavbarSmall /> : <NavbarLarge />}

      {isSmall ? (
        <motion.div className="flex flex-col gap-8" variants={fadeIn}>
          <div className="flex justify-between xl:items-center gap-2 xl:min-h-[80vh] xl:pt-4 pt-2">
            <motion.div
              className="flex flex-col xl:gap-4 gap-2"
              variants={fadeIn}
            >
              <h3
                className="font-semibold"
                style={{
                  fontSize: fontSizes.heading2,
                  color: colors.mainHeading,
                }}
              >
                {t("heroHeading")}
              </h3>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0">
                  <h3
                    className="font-normal"
                    style={{
                      fontSize: fontSizes.heading2,
                      color: colors.mainText,
                    }}
                  >
                    {t("count1")}+
                  </h3>
                  <p
                    style={{
                      color: colors.mainText,
                      fontSize: fontSizes.statNumber,
                    }}
                  >
                    {t("countHeading1")}
                  </p>
                </div>
                <div className="flex flex-col gap-0">
                  <h3
                    className="font-normal"
                    style={{
                      fontSize: fontSizes.heading2,
                      color: colors.mainText,
                    }}
                  >
                    {t("count2")}+
                  </h3>
                  <p
                    style={{
                      color: colors.mainText,
                      fontSize: fontSizes.statNumber,
                    }}
                  >
                    {t("countHeading2")}
                  </p>
                </div>
                <div className="flex flex-col gap-0">
                  <h3
                    className="font-normal"
                    style={{
                      fontSize: fontSizes.heading2,
                      color: colors.mainText,
                    }}
                  >
                    {t("count3")}+
                  </h3>
                  <p
                    style={{
                      color: colors.mainText,
                      fontSize: fontSizes.statNumber,
                    }}
                  >
                    {t("countHeading3")}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="flex flex-col gap-4 items-end"
              variants={fadeIn}
            >
              <motion.img
                src={assetImages.hero1}
                alt=""
                className="w-[140px]"
                variants={fadeIn}
              />
              <div className="relative min-[414px]:w-[180px] w-[165px] h-[100px]">
                <motion.img
                  src={assetIcons.abstractRectangle}
                  alt=""
                  className="absolute min-[414px]:w-16 w-14 -left-10 -top-4"
                  variants={fadeIn}
                />
                {/* <img src={assetImages.hero2} alt="" width={400} className="mr-16" /> */}
                <motion.img
                  src={assetImages.hero2}
                  alt=""
                  className="absolute right-3 w-[180px]"
                  variants={fadeIn}
                />

                <div
                  className="w-10 h-10 absolute right-0 bottom-2 z-30 flex cursor-pointer"
                  style={{
                    backgroundColor: colors.primary,
                  }}
                  onClick={() => handleScroll("services")}
                >
                  <motion.img
                    src={assetIcons.arrowDown}
                    alt=""
                    className="m-auto w-2"
                    variants={fadeIn}
                  />
                </div>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-col gap-6">
            <p
              className="xl:max-w-xl max-w-md"
              style={{
                color: colors.mainText,
                fontSize: fontSizes.body,
                fontWeight: 500,
              }}
            >
              {t("heroText")}
            </p>

            <div className="mx-auto">
              <CtaButton
                label={"Start Project"}
                onClick={() => handleScroll("contact-small")}
                style={{
                  padding: "15px 40px",
                }}
              />
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="flex justify-between xl:items-center gap-3 xl:min-h-[80vh] xl:pt-4 pt-2">
          <div className="flex flex-col xl:gap-4 gap-2">
            <h3
              className="font-semibold"
              style={{
                fontSize: fontSizes.mainHeading,
                color: colors.mainHeading,
              }}
            >
              {t("heroHeading")}
            </h3>

            <p
              className="xl:max-w-xl max-w-md"
              style={{
                color: colors.mainText,
                fontSize: fontSizes.body,
                fontWeight: 500,
              }}
            >
              {t("heroText")}
            </p>

            <div className="xl:mt-10 mt-6">
              <CtaButton
                label={t("heroCta")}
                onClick={() => handleScroll("contact")}
                style={{
                  padding: "15px 40px",
                }}
              />
            </div>

            <div className="flex xl:gap-16 lg:gap-10 md:gap-5 xl:mt-10 mt-6">
              <div className="flex flex-col gap-0">
                <h3
                  className="font-normal"
                  style={{
                    fontSize: fontSizes.heading1,
                    color: colors.mainText,
                  }}
                >
                  {t("count1")}+
                </h3>
                <p
                  style={{
                    color: colors.mainText,
                    fontSize: fontSizes.statNumber,
                  }}
                >
                  {t("countHeading1")}
                </p>
              </div>
              <div className="flex flex-col gap-0">
                <h3
                  className="font-normal"
                  style={{
                    fontSize: fontSizes.heading1,
                    color: colors.mainText,
                  }}
                >
                  {t("count2")}+
                </h3>
                <p
                  style={{
                    color: colors.mainText,
                    fontSize: fontSizes.statNumber,
                  }}
                >
                  {t("countHeading2")}
                </p>
              </div>
              <div className="flex flex-col gap-0">
                <h3
                  className="font-normal"
                  style={{
                    fontSize: fontSizes.heading1,
                    color: colors.mainText,
                  }}
                >
                  {t("count3")}+
                </h3>
                <p
                  style={{
                    color: colors.mainText,
                    fontSize: fontSizes.statNumber,
                  }}
                >
                  {t("countHeading3")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 items-end">
            <img
              src={assetImages.hero1}
              alt=""
              className="xl:w-[340px] lg:w-[270px] w-[230px]"
            />
            <div className="relative xl:w-[450px] lg:w-[340px] w-[290px] xl:h-[274px] lg:h-[216px] h-[184px]">
              <img
                src={assetIcons.abstractRectangle}
                alt=""
                className="absolute w-28 -left-16 -top-6"
              />
              {/* <img src={assetImages.hero2} alt="" width={400} className="mr-16" /> */}
              <img
                src={assetImages.hero2}
                alt=""
                className="absolute xl:right-16 right-12 xl:w-[430px]"
              />

              <div
                className="xl:w-24 lg:w-20 w-16 xl:h-24 lg:h-20 h-16 absolute right-0 bottom-0 z-30 flex cursor-pointer"
                style={{
                  backgroundColor: colors.primary,
                }}
                onClick={() => handleScroll("services")}
              >
                <img
                  src={assetIcons.arrowDown}
                  alt=""
                  className="m-auto xl:w-6 w-4"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Hero;
